import {
  OptionalStartupable,
  Startupable,
  StartupAuthContext,
} from '@/services/base/Startupable';
import * as StartupHelper from '@/services/base/StartupHelper';
import BraintreeApplePayService from '@/services/braintree/BraintreeApplePayService';
import BraintreeCardService from '@/services/braintree/BraintreeCardService';
import BraintreeGooglePayService from '@/services/braintree/BraintreeGooglePayService';
import BraintreePayPalPayLaterService from '@/services/braintree/BraintreePayPalPayLaterService';
import BraintreePayPalService from '@/services/braintree/BraintreePayPalService';
import DenizBankService from '@/services/denizbank/DenizBankService';
import LSVService from '@/services/directdebit/LSVService';
import SepaService from '@/services/directdebit/SepaService';
import ECommerceConnectService from '@/services/ecommerceconnect/ECommerceConnectService';
import InvoiceService from '@/services/invoice/InvoiceService';
import NavigationService from '@/services/navigation/NavigationService';
import PaymentStore from '@/services/payments/PaymentStore';
import SubsidiaryCardViaTerminalOnPickupService from '@/services/subsidiary/SubsidiaryCardViaTerminalOnPickupService';
import SubsidiaryService from '@/services/subsidiary/SubsidiaryService';
import SubsidiaryServiceCashOnPickupService from '@/services/subsidiary/SubsidiaryServiceCashOnPickupService';
import TransbankService from '@/services/transbank/TransbankService';
import WorldpayBancontactService from '@/services/worldpay/WorldpayBancontactService';
import WorldpayCardService from '@/services/worldpay/WorldpayCardService';
import WorldpayIdealService from '@/services/worldpay/WorldpayIdealService';
import WorldpayJCBService from '@/services/worldpay/WorldpayJCBService';
import WorldpayKlarnaService from '@/services/worldpay/WorldpayKlarnaService';
import { getWindowParam } from '@/utils/url';

import BankTransferService from '../banktransfer/BankTransferService';
import BraintreeSepaService from '../braintree/BraintreeSepaService';
import PaymentOnDeliveryService from '../delivery/PaymentOnDeliveryService';
import PostFinanceService from '../directdebit/PostFinanceService';
import FinvoiceService from '../finvoice/FinvoiceService';
import FlutterwaveCardService from '../flutterwave/FlutterwaveCardService';
import NuveiCardService from '../nuvei/NuveiCardService';
import NuveiTwintService from '../nuvei/NuveiTwintService';
import ProcessingKZService from '../processingkz/ProcessingKZService';
import RazorPayService from '../razorpay/RazorPayService';
import TwoCTwoPService from '../twoctwop/TwoCTwoPService';
import YookassaService from '../yookassa/YookassaService';

// List of services we need to start the application
const PaymentServices: Startupable[] = [PaymentStore, NavigationService];

// List of services that should only be started depending on the allowed payment
// methods or user available instruments that he can use.
const OptionalServices: OptionalStartupable[] = [
  BraintreeApplePayService,
  BraintreeCardService,
  BraintreeGooglePayService,
  BraintreePayPalPayLaterService,
  BraintreePayPalService,

  WorldpayBancontactService,
  WorldpayCardService,
  WorldpayIdealService,
  WorldpayJCBService,
  WorldpayKlarnaService,
  SepaService,
  LSVService,
  PostFinanceService,

  SubsidiaryService,
  SubsidiaryServiceCashOnPickupService,
  SubsidiaryCardViaTerminalOnPickupService,
  PaymentOnDeliveryService,
  BankTransferService,
  FinvoiceService,
  TransbankService,
  ECommerceConnectService,
  NuveiTwintService,
  FlutterwaveCardService,
  NuveiCardService,
  BraintreeSepaService,
  TwoCTwoPService,
  ProcessingKZService,
  YookassaService,
  RazorPayService,
  DenizBankService,
  InvoiceService,
];

export type TBootstrapMode = 'payment-intent' | 'payment-manage';

class ServiceRegistrar {
  public async startServicesFor(
    bootstrapMode: TBootstrapMode,
    authContext?: StartupAuthContext,
  ) {
    await StartupHelper.startInSequence(
      PaymentServices,
      bootstrapMode,
      authContext,
    );

    const legalEntitySearchParam = getWindowParam('le');
    const legalEntityNumber = !isNaN(Number(legalEntitySearchParam))
      ? Number(legalEntitySearchParam)
      : undefined;

    if (
      bootstrapMode === 'payment-intent' ||
      (bootstrapMode === 'payment-manage' && legalEntityNumber !== undefined)
    ) {
      await StartupHelper.startInSequence(
        OptionalServices,
        bootstrapMode,
        authContext,
      );
      this.handleFailedOptionalServices();
    }
  }

  private handleFailedOptionalServices() {
    const runningPaymentModules = OptionalServices.filter((o) =>
      StartupHelper.isStartupableStarted(o),
    ).map((o) => o.moduleId);
    PaymentStore.forceUpdateInstrumentStatus(runningPaymentModules);
  }
}

export default new ServiceRegistrar();
